import { useDispatch } from 'react-redux';
import { BillType, DisplayBillState, useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import { DropDownButton, DropdownItem, Icon, IconName } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { ProcessPaymentType } from '../../../../payment';
import { refreshPage } from '../../../../app/actions/common';
import { useProcessPaymentDialog } from '../../../../payment/components/forms/ProcessPayment/useProcessPaymentDialog';
import { useAllowedPaymentMethodsForInvoices } from './constants';

export interface BillProps {
    id: string;
    ownerId: string;
    type: BillType;
    displayState: DisplayBillState;
}

interface OwnProps {
    bill: BillProps;
    refetch?: () => void;
}

interface OwnPropsWithAllowedPaymentTypes extends OwnProps {
    allowedPaymentTypes?: ProcessPaymentType[];
}

export const RowActions = ({ bill, refetch, allowedPaymentTypes }: OwnPropsWithAllowedPaymentTypes) => {
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();
    const [renderProcessPaymentDialog, toggleProcessPaymentDialog] = useProcessPaymentDialog({
        accountId: bill.ownerId,
        defaultSelectedFormValues: { invoiceIds: [bill.id] },
        allowedPaymentTypes,
        canLinkInvoices: true,
        onFormSuccess: () => {
            refetch?.();

            void invalidateRepositoryQuery('invoices', 'getAll', {
                ownerIds: [bill.ownerId],
            });
        },
    });

    if (
        bill.type !== BillType.Invoice ||
        [DisplayBillState.Closed, DisplayBillState.Paid].includes(bill.displayState)
    ) {
        return null;
    }

    return (
        <>
            <DropDownButton
                icon={IconName.More}
                variant="ghost"
                disabled={bill.displayState === DisplayBillState.ProcessingPayment}
            >
                <DropdownItem
                    leftDetail={<Icon value={IconName.Payments} />}
                    label={lt('ProcessPaymentPanelTitleForOptions')}
                    onClick={toggleProcessPaymentDialog}
                />
            </DropDownButton>

            {renderProcessPaymentDialog()}
        </>
    );
};

export const CompanyRowActions = (props: OwnProps) => (
    <RowActions {...props} allowedPaymentTypes={[ProcessPaymentType.CashPayment, ProcessPaymentType.ExternalPayment]} />
);

export const RowActionsBEComponent = ({ bill, isCompany }: { bill: BillProps; isCompany: boolean }) => {
    const dispatch = useDispatch();
    const Component = isCompany ? CompanyRowActions : RowActions;

    const allowedPaymentTypes = useAllowedPaymentMethodsForInvoices();

    return <Component bill={bill} refetch={() => dispatch(refreshPage())} allowedPaymentTypes={allowedPaymentTypes} />;
};
